

import { useCallback, useState } from "react";
import { useEvents } from "@/providers/EventsProvider";
import { useNavigate as useRouter } from "react-router-dom";
import { useTranslations } from "next-intl";
import { useGuestSeedToasts } from "@/hooks/useGuestSeedToasts";
import { describeBulkError, describeSingleFailure } from "@/lib/bulk/bulkErrorMessage";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import { normalizeBulkOutcome } from "@/lib/bulk/bulkFailure";
import { useToast } from "@/components/Toast/Toast";

/** Items that act on the row. Everything else navigates away. */
const ACTION_ITEMS = new Set(["archive", "unarchive", "duplicate", "delete"]);

/** A refusal the reader has to take in, not a status word (Toast default 3 s). */
const ROW_REFUSAL_TOAST_MS = 6000;

export const useCTA = ({ eventId }: { eventId: string }) => {
  const router = useRouter();
  const t3 = useTranslations("duplicateEvent");
  const tErrors = useTranslations("errors");
  const tBulk = useTranslations("bulkConfirm");
  const announceGuestSeeds = useGuestSeedToasts();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirmation, setDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>();
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [duplicateError, setDuplicateError] = useState<string | undefined>();
  const [showDuplicateToast, setShowDuplicateToast] = useState(false);
  const { archiveEvents, unarchiveEvents, deleteEvents, duplicateEvent } =
    useEvents();

  const handleDropdownAction = useCallback(
    // Handle dropdown action selection
    async (item: string, e?: React.MouseEvent) => {
      // Prevent event propagation if event exists
      if (e) e.stopPropagation();

      if (!eventId) return;

      // Set loading state before any operations
      setIsLoading(true);
      setActionError(undefined);

      // Archive and unarchive have no dialog: their refusal is a toast. It
      // used to go into `actionError`, which only the delete dialog renders,
      // so a refused archive stopped the spinner and said nothing.
      const isArchiveItem = item === "archive" || item === "unarchive";
      try {
        if (isArchiveItem) {
          const run = item === "archive" ? archiveEvents : unarchiveEvents;
          const outcome = normalizeBulkOutcome(
            await run({ mode: "ids", ids: [eventId] }),
          );
          // The server answers 200 with a per-row outcome, so `failed > 0`
          // arrives as a successful response. Ignoring it reported an archive
          // that never happened as done. The reason's own sentence, not "try
          // again"; `raceLost` (someone else archived it first) is no failure.
          const refused = outcome?.failures.filter((f) => f.reason !== "raceLost") ?? [];
          // No rows at all is still a refusal (the generic line); only an
          // all-raceLost answer means the event is archived already.
          if (outcome && outcome.failed > 0 && (refused.length > 0 || outcome.failures.length === 0)) {
            showToast(describeSingleFailure(refused, { tErrors, tBulk }), "error", ROW_REFUSAL_TOAST_MS);
          }
        }
        else if (item === "duplicate") {
          setDuplicateError(undefined);
          setShowDuplicateModal(true);
        }
        else if (item === "delete") {
          setDeleteConfirmation(true);
        } else {

          router(`/event/${eventId}/${item}`);
        }
      } catch (error) {
        const text = describeBulkError(error, { tErrors, tBulk });
        if (isArchiveItem) showToast(text, "error", ROW_REFUSAL_TOAST_MS);
        else setActionError(text);
      } finally {
        // Navigation keeps the spinner up until the route actually swaps;
        // everything else has to clear it here. This used to `return` for
        // every item without a slash, so archive/unarchive NEVER reset it —
        // masked only because a successful row leaves the list and unmounts
        // this component. The moment a failed row stays put, the covering
        // spinner sits on it forever.
        if (ACTION_ITEMS.has(item)) setIsLoading(false);
        else setTimeout(() => setIsLoading(false), 500);
      }
    },
    [eventId, archiveEvents, unarchiveEvents, router, tBulk, tErrors, showToast],
  );

  const handleNestedAction = useCallback(
    // Handle nested actions (for dropdowns with sub-items)
    (parentItem: string, subItem: string, e?: React.MouseEvent) => {
      // Prevent event propagation if event exists
      if (e) {
        e.stopPropagation();
      }

      if (!eventId) return;

      // Set loading state
      setIsLoading(true);

      try {
        if (parentItem === "guests/guestlist") {
          router(`/event/${eventId}/guests/${subItem}`);
        } else if (parentItem === "messaging/email-templates") {
          router(`/event/${eventId}/messaging/${subItem}`);
        } else if (parentItem === "settings") {
          router(`/event/${eventId}/settings/${subItem}`);
        } else if (parentItem === "check-in") {
          router(`/event/${eventId}/check-in/${subItem}`);
        } else if (parentItem === "automation") {
          router(`/event/${eventId}/automation/${subItem}`);
        }
      } catch (error) {
        console.error(
          `Error handling nested action ${parentItem}/${subItem}:`,
          error,
        );
      }

      // Don't reset loading state here - it should stay visible during navigation
    },
    [eventId, router],
  );

  const handleCancelDelete = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling up
    setDeleteConfirmation(false);
    setActionError(undefined);
  }, []);

  const handleDeleteConfirmed = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling up
    if (!eventId) return;

    setIsDeleting(true);
    setActionError(undefined);

    try {
      const outcome = normalizeBulkOutcome(
        await deleteEvents({ mode: "ids", ids: [eventId] }),
      );
      // `failed > 0` comes back as a 200. Both this and the catch below used
      // to end in the same `finally`, which closed the dialog either way — so
      // a refused delete was indistinguishable from a successful one.
      if (outcome && outcome.failed > 0) {
        setActionError(describeSingleFailure(outcome.failures, { tErrors, tBulk }));
        return;
      }
      setDeleteConfirmation(false);
    } catch (error) {
      setActionError(describeBulkError(error, { tErrors, tBulk }));
    } finally {
      setIsDeleting(false);
    }
  }, [eventId, deleteEvents, tBulk, tErrors]);

  const handleDuplicateConfirmed = useCallback(async (name: string, copyGuests: boolean) => {
    if (!eventId) return;
    setIsDuplicating(true);
    setDuplicateError(undefined);
    try {
      const outcome = await duplicateEvent(eventId, name, copyGuests);
      setShowDuplicateModal(false);
      setShowDuplicateToast(true);
      setTimeout(() => setShowDuplicateToast(false), 3000);
      announceGuestSeeds(outcome);
    } catch (error) {
      console.error("Error duplicating event:", error);
      // M-08: the code's own sentence (errors.eventQuotaExceeded), else the
      // dialog's own line — never "Error: <raw code>".
      setDuplicateError(
        translateBackendError(tErrors, error instanceof Error ? error : null, t3("error")),
      );
    } finally {
      setIsDuplicating(false);
    }
  }, [eventId, duplicateEvent, t3, tErrors, announceGuestSeeds]);

  const handleCancelDuplicate = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDuplicateModal(false);
    setDuplicateError(undefined);
  }, []);

  return {
    router,
    isLoading,
    isDeleting,
    actionError,
    showDeleteConfirmation,
    handleCancelDelete,
    handleDeleteConfirmed,
    handleDropdownAction,
    handleNestedAction,
    showDuplicateModal,
    isDuplicating,
    duplicateError,
    showDuplicateToast,
    handleDuplicateConfirmed,
    handleCancelDuplicate,
  };
};
